import React, { useRef, useState, useEffect } from 'react';
import './ProductCate.css';
import { MdOutlineStar, MdArrowForward } from 'react-icons/md';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const ProductCate = () => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const categories = [
    {
      id: 1,
      title: "Clothing Brands",
      description: "Modest wear, streetwear, kidswear",
      image: "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=480&q=80",
      badge: "Fashion",
      badgeColor: "primary"
    },
    {
      id: 2,
      title: "Shoes",
      description: "Sneakers, heels, loafers, kids",
      image: "https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?auto=format&fit=crop&w=480&q=80",
      badge: "Footwear",
      badgeColor: "coral"
    },
    {
      id: 3,
      title: "Jewelry",
      description: "Gold, silver, gemstones, daily wear",
      image: "https://images.unsplash.com/photo-1611652022419-a9419f74343d?auto=format&fit=crop&w=480&q=80",
      badge: "Luxe",
      badgeColor: "sunny"
    },
    {
      id: 4,
      title: "Cosmetics",
      description: "Skincare, makeup, fragrance, hair",
      image: "https://images.unsplash.com/photo-1522335789203-aaa2f6c5d7a8?auto=format&fit=crop&w=480&q=80",
      badge: "Beauty",
      badgeColor: "accent"
    },
    {
      id: 5,
      title: "Food Items",
      description: "Bakery, snacks, gift hampers",
      image: "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=480&q=80",
      badge: "F&B",
      badgeColor: "coral"
    },
    {
      id: 6,
      title: "Accessories",
      description: "Bags, watches, sunglasses, belts",
      image: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?auto=format&fit=crop&w=480&q=80",
      badge: "Accessory",
      badgeColor: "primary"
    },
    {
      id: 7,
      title: "Home Products",
      description: "Decor, kitchen, candles, linen",
      image: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=480&q=80",
      badge: "Home",
      badgeColor: "sunny"
    },
    {
      id: 8,
      title: "Instagram & WhatsApp Sellers",
      description: "Move from DMs to a real store",
      image: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=480&q=80",
      badge: "Social",
      badgeColor: "accent"
    },
    {
      id: 9,
      title: "Physical Stores Online",
      description: "Take your retail brand digital",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=480&q=80",
      badge: "Retail",
      badgeColor: "dark"
    }
  ];

  const getBadgeClass = (color) => {
    switch(color) {
      case 'primary': return 'badge-primary';
      case 'coral': return 'badge-coral';
      case 'sunny': return 'badge-sunny';
      case 'accent': return 'badge-accent';
      case 'dark': return 'badge-dark';
      default: return 'badge-primary';
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 20);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 20);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <section className="product-categories-section">
      <div className="product-categories-container">
        {/* Header */}
        <div className="product-categories-header">
          <div className="product-categories-badge">
            <MdOutlineStar size={11} />
            <span>Who It's For</span>
          </div>
          <h2 className="product-categories-title">
            Built for <span className="text-gradient">Product-Based Businesses</span>
          </h2>
          <p className="product-categories-subtitle">
            Designed for small and growing businesses that want to sell products through their own website.
          </p>
        </div>

        {/* Carousel */}
        <div className="carousel-container">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button 
              className="carousel-arrow carousel-arrow-left"
              onClick={() => scroll('left')}
              aria-label="Previous"
            >
              <FiChevronLeft size={20} />
            </button>
          )}

          {/* Cards Wrapper */}
          <div className="carousel-wrapper">
            <div className="carousel-track" ref={scrollContainerRef}>
              {categories.map((category) => (
                <div key={category.id} className="product-card">
                  <div className="product-card-image">
                    <img src={category.image} alt={category.title} loading="lazy" />
                    <div className="product-card-overlay"></div>
                  </div>
                  <span className={`product-card-badge ${getBadgeClass(category.badgeColor)}`}>
                    {category.badge}
                  </span>
                  <div className="product-card-content">
                    <h3 className="product-card-title">{category.title}</h3>
                    <p className="product-card-description">{category.description}</p>
                    <div className="product-card-link">
                      Launch online <MdArrowForward size={12} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button 
              className="carousel-arrow carousel-arrow-right"
              onClick={() => scroll('right')}
              aria-label="Next"
            >
              <FiChevronRight size={20} />
            </button>
          )}
        </div>

        {/* Footer Note */}
        <p className="product-categories-footer">
          If you sell products, we can help you turn your business into a proper online store.
        </p>
      </div>
    </section>
  );
};

export default ProductCate;